import { ArchitectureNode, ArchitectureEdge, ComponentType } from '../types';

export interface ValidationIssue {
  type: 'error' | 'warning' | 'info';
  message: string;
  nodeIds?: string[];
  edgeIds?: string[];
  suggestion?: string;
}

export const validateConnection = (sourceType: string, targetType: string, connectionType: string, port?: string): { valid: boolean; warning?: string } => {
  // Port validation
  if (port) {
    const portNum = parseInt(port, 10);
    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
      return { valid: false, warning: `Invalid port number: ${port}. Must be between 1 and 65535.` };
    }
  }

  // Type compatibility checks (basic rules)
  const incompatibleTypes: Record<string, string[]> = {
    'JDBC': ['WebServer', 'ApiGateway', 'LoadBalancer'], // JDBC should only connect to databases
  };

  if (incompatibleTypes[connectionType]?.includes(targetType)) {
    return { valid: false, warning: `${connectionType} connections are typically not used with ${targetType} components.` };
  }

  // Connection type specific validations
  if (connectionType === 'JDBC' && !targetType.includes('RDBMS') && targetType !== 'Database') {
    return { valid: true, warning: `JDBC is typically used for database connections, but ${targetType} is not a database type.` };
  }

  if ((connectionType === 'HTTP' || connectionType === 'HTTPS') && targetType === 'RDBMS') {
    return { valid: true, warning: `HTTP/HTTPS connections to databases are uncommon. Consider using JDBC instead.` };
  }

  return { valid: true };
};

export const validateArchitecture = (
  nodes: ArchitectureNode[],
  edges: ArchitectureEdge[]
): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const edgeMap = new Map(edges.map(e => [e.id, e]));

  // 1. Check for orphaned components (no connections)
  const connectedNodeIds = new Set<string>();
  edges.forEach(edge => {
    connectedNodeIds.add(edge.source);
    connectedNodeIds.add(edge.target);
  });

  const orphanedNodes = nodes.filter(n => 
    !connectedNodeIds.has(n.id) && n.data.type !== ComponentType.GROUP
  );
  if (orphanedNodes.length > 0) {
    issues.push({
      type: 'warning',
      message: `${orphanedNodes.length} component(s) have no connections`,
      nodeIds: orphanedNodes.map(n => n.id),
      suggestion: 'Consider connecting these components or removing them if unused'
    });
  }

  // 2. Check for missing load balancers with multiple instances
  const componentCounts = new Map<string, number>();
  nodes.forEach(node => {
    if (node.data.type !== ComponentType.GROUP) {
      const key = node.data.type;
      componentCounts.set(key, (componentCounts.get(key) || 0) + 1);
    }
  });

  const hasLoadBalancer = nodes.some(n => n.data.type === ComponentType.LOAD_BALANCER);
  const hasMultipleWebServers = (componentCounts.get(ComponentType.WEB_SERVER) || 0) > 1;
  const hasMultipleMicroservices = (componentCounts.get(ComponentType.MICROSERVICE) || 0) > 1;

  if ((hasMultipleWebServers || hasMultipleMicroservices) && !hasLoadBalancer) {
    issues.push({
      type: 'warning',
      message: 'Multiple instances detected but no load balancer',
      suggestion: 'Consider adding a LoadBalancer component to distribute traffic'
    });
  }

  // 3. Check for databases without backup/replication indicators
  const databases = nodes.filter(n => n.data.type === ComponentType.DATABASE);
  databases.forEach(db => {
    if (!db.data.specs?.engine?.toLowerCase().includes('replica') && 
        !db.data.specs?.engine?.toLowerCase().includes('backup')) {
      issues.push({
        type: 'info',
        message: `Database "${db.data.label}" may need backup/replication`,
        nodeIds: [db.id],
        suggestion: 'Consider adding replication or backup strategy'
      });
    }
  });

  // 4. Check for missing authentication on public-facing services
  const publicServices = nodes.filter(n => 
    n.data.type === ComponentType.API_GATEWAY || 
    n.data.type === ComponentType.WEB_SERVER
  );
  const hasAuthService = nodes.some(n => n.data.type === ComponentType.AUTH_SERVICE);
  
  if (publicServices.length > 0 && !hasAuthService) {
    issues.push({
      type: 'warning',
      message: 'Public-facing services detected but no authentication service',
      nodeIds: publicServices.map(n => n.id),
      suggestion: 'Consider adding an AuthService component for security'
    });
  }

  // 5. Check for circular dependencies
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  
  const hasCycle = (nodeId: string): boolean => {
    if (recursionStack.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;
    
    visited.add(nodeId);
    recursionStack.add(nodeId);
    
    const outgoingEdges = edges.filter(e => e.source === nodeId);
    for (const edge of outgoingEdges) {
      if (hasCycle(edge.target)) {
        return true;
      }
    }
    
    recursionStack.delete(nodeId);
    return false;
  };

  for (const node of nodes) {
    if (!visited.has(node.id) && hasCycle(node.id)) {
      issues.push({
        type: 'error',
        message: 'Circular dependency detected in architecture',
        suggestion: 'Review connections to break circular dependencies'
      });
      break;
    }
  }

  // 6. Check for unreachable components
  const reachable = new Set<string>();
  const startNodes = nodes.filter(n => 
    n.data.type === ComponentType.API_GATEWAY || 
    n.data.type === ComponentType.LOAD_BALANCER ||
    edges.filter(e => e.target === n.id).length === 0 // No incoming edges
  );

  const dfs = (nodeId: string) => {
    if (reachable.has(nodeId)) return;
    reachable.add(nodeId);
    edges.filter(e => e.source === nodeId).forEach(e => dfs(e.target));
  };

  startNodes.forEach(n => dfs(n.id));
  const unreachable = nodes.filter(n => 
    !reachable.has(n.id) && 
    n.data.type !== ComponentType.GROUP &&
    startNodes.length > 0
  );

  if (unreachable.length > 0) {
    issues.push({
      type: 'warning',
      message: `${unreachable.length} component(s) may be unreachable`,
      nodeIds: unreachable.map(n => n.id),
      suggestion: 'Check if these components should be connected to the main flow'
    });
  }

  // 7. Check for missing cache on high-traffic paths
  const hasCache = nodes.some(n => n.data.type === ComponentType.CACHE);
  const hasDatabase = nodes.some(n => n.data.type === ComponentType.DATABASE);
  const hasHighTrafficServices = nodes.some(n => 
    (n.data.type === ComponentType.WEB_SERVER || n.data.type === ComponentType.API_GATEWAY) &&
    edges.filter(e => e.target === n.id).length > 2
  );

  if (hasDatabase && hasHighTrafficServices && !hasCache) {
    issues.push({
      type: 'info',
      message: 'Consider adding a cache layer for better performance',
      suggestion: 'Add a Cache component between services and database'
    });
  }

  // 8. Check for components without specifications
  const componentsWithoutSpecs = nodes.filter(n => 
    n.data.type !== ComponentType.GROUP &&
    (!n.data.specs || (!n.data.specs.cpu && !n.data.specs.memory))
  );

  if (componentsWithoutSpecs.length > 0) {
    issues.push({
      type: 'info',
      message: `${componentsWithoutSpecs.length} component(s) missing specifications`,
      nodeIds: componentsWithoutSpecs.map(n => n.id),
      suggestion: 'Add CPU and memory specs for better resource planning'
    });
  }

  return issues;
};
